// process.env safe client component
import React, { useState } from 'react';
import {
  Shield,
  Lock,
  Mail,
  User,
  Phone,
  Eye,
  EyeOff,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Zap,
  Bot
} from 'lucide-react';

interface LunaAuthWidgetProps {
  tenantApiKey: string;
  tenantName?: string;
  onSuccess?: (user: any, tenantApiKey: string) => void;
  onClose?: () => void;
  customRedirectUrl?: string;
}

export const LunaAuthWidget: React.FC<LunaAuthWidgetProps> = ({
  tenantApiKey,
  tenantName = 'Client Portal',
  onSuccess,
  onClose,
  customRedirectUrl
}) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // ── Input Sanitization Helper ──────────────────────────────────────────
  const sanitize = (val: string) => val.replace(/<[^>]*>?/gm, '').trim();

  // ── Password Strength Evaluator ────────────────────────────────────────
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[a-zA-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
    return score;
  };

  const strengthScore = getPasswordStrength(password);

  // ── Handle Submit (Zero-Trust Endpoint Invocation) ──────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    const cleanEmail = sanitize(email).toLowerCase();
    const cleanPassword = password.trim();
    const cleanFullName = sanitize(fullName);
    const cleanPhone = sanitize(phone);

    if (!cleanEmail || !cleanPassword) {
      setError('Please enter both email and password.');
      return;
    }

    if (cleanEmail.length > 100) {
      setError('Email must be under 100 characters.');
      return;
    }

    if (cleanPassword.length < 8 || cleanPassword.length > 64) {
      setError('Password must be between 8 and 64 characters.');
      return;
    }

    if (mode === 'register' && (!cleanFullName || cleanFullName.length > 100)) {
      setError('Please provide your full name (max 100 characters).');
      return;
    }

    setLoading(true);

    try {
      const endpoint = mode === 'register' 
        ? '/api/tenet/end-user/auth/register' 
        : '/api/tenet/end-user/auth/login';

      const payload = {
        tenantApiKey: sanitize(tenantApiKey),
        email: cleanEmail,
        password: cleanPassword,
        ...(mode === 'register' ? { fullName: cleanFullName, phone: cleanPhone } : {})
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Ensures HttpOnly cookie is accepted & stored securely
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Authentication failed. Please check your credentials.');
      }

      setSuccessMsg(data.message || 'Access granted. Redirecting...');

      // Store non-sensitive profile strictly in sessionStorage (Zero-Trust: NEVER in localStorage)
      const userProfile = {
        id: data.user?.id,
        name: data.user?.fullName || cleanFullName || cleanEmail.split('@')[0],
        email: data.user?.email || cleanEmail,
        role: data.user?.role || 'Verified Corporate Client',
        tenantId: data.user?.tenantId,
        signedInAt: new Date().toISOString()
      };
      sessionStorage.setItem('client_portal_user', JSON.stringify(userProfile));

      // Handoff to callback or redirect smoothly
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(userProfile, data.tenantApiKey || tenantApiKey);
        } else {
          const targetKey = encodeURIComponent(data.tenantApiKey || tenantApiKey);
          const redirectTarget = customRedirectUrl || `/portal/${targetKey}`;
          window.location.href = redirectTarget;
        }
      }, 700);

    } catch (err: any) {
      console.warn('[LunaAuthWidget] Error:', err);
      setError(err?.message || 'Unable to connect to authentication server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-[#0D121F]/95 border border-cyan-500/30 rounded-3xl p-7 sm:p-8 backdrop-blur-2xl shadow-[0_0_50px_rgba(6,182,212,0.2)] text-slate-100 font-sans relative overflow-hidden select-none animate-in fade-in zoom-in-95 duration-200">
      
      {/* Glowing Ambient Gradient Accents */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

      {/* Top Header & Tenant Identity */}
      <div className="flex items-center justify-between pb-4 border-b border-white/5 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
            <Shield size={20} />
          </div>
          <div>
            <h3 className="text-sm font-black text-white font-sans uppercase tracking-tight truncate max-w-[200px]">
              {tenantName}
            </h3>
            <p className="text-[10px] text-cyan-400 font-mono flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Zero-Trust Gateway
            </p>
          </div>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            ✕
          </button>
        )}
      </div>

      {/* Mode Switch Tabs */}
      <div className="grid grid-cols-2 gap-1.5 p-1 bg-[#080C14] rounded-2xl border border-white/5 my-5 relative z-10">
        <button
          type="button"
          onClick={() => { setMode('login'); setError(null); }}
          className={`py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
            mode === 'login'
              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Lock size={12} />
          <span>Client Sign In</span>
        </button>

        <button
          type="button"
          onClick={() => { setMode('register'); setError(null); }}
          className={`py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
            mode === 'register'
              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <User size={12} />
          <span>Register Account</span>
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2 mb-4 animate-in fade-in">
          <AlertCircle size={15} className="shrink-0 text-rose-400" />
          <span className="leading-snug">{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2 mb-4 animate-in fade-in">
          <CheckCircle2 size={15} className="shrink-0 text-emerald-400" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Auth Form */}
      <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
        {mode === 'register' && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
              <User size={12} className="text-cyan-400" /> Full Legal Name
            </label>
            <input
              type="text"
              required
              maxLength={100}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Alexander Vance"
              className="w-full bg-[#080C14] border border-cyan-500/30 focus:border-cyan-400 rounded-xl px-3.5 py-2.5 text-xs text-cyan-100 focus:outline-none transition shadow-inner"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
            <Mail size={12} className="text-cyan-400" /> Corporate Email Address
          </label>
          <input
            type="email"
            required
            maxLength={100}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alexander@vanceholdings.com"
            className="w-full bg-[#080C14] border border-cyan-500/30 focus:border-cyan-400 rounded-xl px-3.5 py-2.5 text-xs text-cyan-100 focus:outline-none transition shadow-inner"
          />
        </div>

        {mode === 'register' && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
              <Phone size={12} className="text-cyan-400" /> Mobile Number <span className="text-slate-500 text-[10px]">(Optional)</span>
            </label>
            <input
              type="tel"
              maxLength={50}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 019-2834"
              className="w-full bg-[#080C14] border border-cyan-500/30 focus:border-cyan-400 rounded-xl px-3.5 py-2.5 text-xs text-cyan-100 focus:outline-none transition shadow-inner font-mono"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
            <Lock size={12} className="text-cyan-400" /> Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength={8}
              maxLength={64}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className="w-full bg-[#080C14] border border-cyan-500/30 focus:border-cyan-400 rounded-xl pl-3.5 pr-10 py-2.5 text-xs text-cyan-100 focus:outline-none transition shadow-inner font-mono"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300 transition cursor-pointer"
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>

          {/* Password Strength Indicator (Register Mode) */}
          {mode === 'register' && password.length > 0 && (
            <div className="space-y-1 pt-1">
              <div className="grid grid-cols-4 gap-1">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`h-1 rounded-full transition-all ${
                      strengthScore >= step
                        ? step <= 2
                          ? 'bg-amber-400'
                          : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]'
                        : 'bg-slate-800'
                    }`}
                  />
                ))}
              </div>
              <p className="text-[10px] text-slate-400 font-mono">
                {strengthScore < 3 ? 'Weak (Add letters & numbers)' : 'Strong password'}
              </p>
            </div>
          )}
        </div>

        {/* Submit Action Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-2xl text-xs font-black tracking-wide bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 flex items-center justify-center gap-2 transition cursor-pointer shadow-[0_0_25px_rgba(6,182,212,0.35)] disabled:opacity-50 mt-2"
        >
          {loading ? (
            <RefreshCw size={14} className="animate-spin text-slate-950" />
          ) : (
            <Zap size={14} />
          )}
          <span>
            {loading
              ? 'Verifying Zero-Trust Rails...'
              : mode === 'register'
              ? 'Create Secure Client Account'
              : 'Enter Secure Client Portal'}
          </span>
        </button>
      </form>

      {/* Security Footer */}
      <div className="pt-4 mt-4 border-t border-white/5 text-center space-y-1.5 relative z-10">
        <p className="text-[10px] text-slate-400 font-mono flex items-center justify-center gap-1.5">
          <Bot size={12} className="text-cyan-400" />
          <span>Encrypted via Luna AI Multi-Tenant Vault</span>
        </p>
        <p className="text-[9px] text-slate-500 font-mono">
          HttpOnly • SameSite=Strict • Zero LocalStorage Secrets
        </p>
      </div>
    </div>
  );
};
